import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { FaCheck, FaClock, FaSitemap } from "react-icons/fa";
import config from "../../../../../config.json";

const API_URL = import.meta.env.VITE_REACT_APP_BACKEND_URL || config.dev;

function authHeaders() {
  const token = localStorage.getItem("tokenLogin");
  return token ? { Authorization: `Bearer ${token}` } : {};
}

function FranchiseJoinCard({ variant, icon, title, description, badge, children }) {
  return (
    <article className={`franchise-join-card is-${variant}`}>
      <div className="franchise-join-card__head">
        <span className="franchise-join-card__icon" aria-hidden>
          {icon}
        </span>
        <div className="franchise-join-card__copy">
          <div className="franchise-join-card__title-row">
            <h3>{title}</h3>
            {badge ? (
              <span className={`franchise-join-card__badge is-${variant}`}>{badge}</span>
            ) : null}
          </div>
          {description ? <p>{description}</p> : null}
        </div>
      </div>
      {children ? <div className="franchise-join-card__body">{children}</div> : null}
    </article>
  );
}

export default function JoinFranchiseSection({ restaurantData }) {
  const { t } = useTranslation();
  const [codeFr, setCodeFr] = useState("");
  const [status, setStatus] = useState("none");
  const [joinInfo, setJoinInfo] = useState(null);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const loadStatus = useCallback(async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API_URL}/franchise/join-request/my-status`, {
        headers: authHeaders(),
      });
      const data = response.data?.data || { status: "none" };
      setStatus(data.status);
      setJoinInfo(data);
    } catch (error) {
      console.error("Failed to load franchise join status:", error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void loadStatus();
  }, [loadStatus, restaurantData?._id]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    const normalizedCode = codeFr.trim().toUpperCase();

    if (!normalizedCode) {
      toast.warning(t("franchiseJoinCodeRequired"));
      return;
    }

    setSubmitting(true);
    try {
      const response = await axios.post(
        `${API_URL}/franchise/join-request`,
        { codeFr: normalizedCode },
        { headers: authHeaders() }
      );
      toast.success(
        response.data?.message ||
          (response.data?.data?.emailSent === false
            ? t("franchiseJoinRequestCreatedNoEmail")
            : t("franchiseJoinRequestSent"))
      );
      setCodeFr("");
      void loadStatus();
    } catch (error) {
      toast.error(
        error.response?.data?.error ||
          error.response?.data?.message ||
          t("franchiseJoinRequestFailed")
      );
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return (
      <FranchiseJoinCard
        variant="loading"
        icon={<FaSitemap />}
        title={t("franchiseJoinTitle")}
        description={t("franchiseJoinLoading")}
      />
    );
  }

  if (status === "joined") {
    const name = joinInfo?.franchise?.nameFr || "-";
    const code = joinInfo?.franchise?.codeFr || "-";
    return (
      <FranchiseJoinCard
        variant="joined"
        icon={<FaCheck />}
        title={t("franchiseJoinTitle")}
        description={t("franchiseJoinAlreadyMember", { name })}
        badge={t("franchiseJoinMemberBadge")}
      >
        <dl className="franchise-join-meta">
          <div>
            <dt>{t("franchiseJoinNameLabel")}</dt>
            <dd>{name}</dd>
          </div>
          <div>
            <dt>{t("franchiseJoinCodeLabel")}</dt>
            <dd>
              <code className="franchise-join-code">{code}</code>
            </dd>
          </div>
        </dl>
      </FranchiseJoinCard>
    );
  }

  if (status === "pending") {
    const name = joinInfo?.joinRequest?.franchiseId?.nameFr || "-";
    const code = joinInfo?.joinRequest?.codeFr || "-";
    return (
      <FranchiseJoinCard
        variant="pending"
        icon={<FaClock />}
        title={t("franchiseJoinTitle")}
        description={t("franchiseJoinPending", { name })}
        badge={t("franchiseJoinPendingBadge")}
      >
        <dl className="franchise-join-meta">
          <div>
            <dt>{t("franchiseJoinNameLabel")}</dt>
            <dd>{name}</dd>
          </div>
          <div>
            <dt>{t("franchiseJoinCodeLabel")}</dt>
            <dd>
              <code className="franchise-join-code">{code}</code>
            </dd>
          </div>
        </dl>
      </FranchiseJoinCard>
    );
  }

  return (
    <FranchiseJoinCard
      variant="idle"
      icon={<FaSitemap />}
      title={t("franchiseJoinTitle")}
      description={t("franchiseJoinDescription")}
    >
      <form onSubmit={handleSubmit} className="franchise-join-form">
        <label className="franchise-join-form__field">
          <span>{t("franchiseJoinCodeLabel")}</span>
          <input
            type="text"
            autoComplete="off"
            spellCheck="false"
            placeholder="FR-ABC123"
            value={codeFr}
            onChange={(event) => setCodeFr(event.target.value.toUpperCase())}
          />
        </label>
        <button type="submit" className="franchise-join-form__submit" disabled={submitting}>
          {submitting ? t("franchiseJoinSending") : t("franchiseJoinSendRequest")}
        </button>
      </form>
    </FranchiseJoinCard>
  );
}
